import { Button, Checkbox, Flex, Spin, Typography } from 'antd'
import { useTranslation } from 'react-i18next'

import { ContentSpinner, QueryErrorAlert } from 'src/components'
import type { ScopeRow } from 'src/features/workspaces/components/agents/scopeModel'
import { ScopePicker } from 'src/features/workspaces/components/agents/ScopePicker'

import { useServiceToggle } from './hooks'
import type { ServiceOption } from './model'
import { useStyle } from './styles'
import type { McpServiceId } from './types'

const { Text } = Typography

type Props = {
  workspaceId: number
  options: ServiceOption[]
  value: McpServiceId[]
  disabled: boolean
  onChange: (services: McpServiceId[]) => void
  catalog: { isLoading: boolean; isError: boolean; refetch: () => void }
  rowsFor: (service: McpServiceId) => ScopeRow[]
  scopesFor: (service: McpServiceId) => Set<string>
  onScopesChange: (service: McpServiceId, scopes: Set<string>) => void
}

/** A picked service opens its scopes right in its card. */
export const ServicePicker = ({
  workspaceId,
  options,
  value,
  disabled,
  onChange,
  catalog,
  rowsFor,
  scopesFor,
  onScopesChange
}: Props) => {
  const { t } = useTranslation()
  const { styles, cx } = useStyle()
  const power = useServiceToggle(workspaceId)

  const toggle = (service: McpServiceId, checked: boolean) =>
    onChange(checked ? [...value, service] : value.filter((s) => s !== service))

  // An admin ticking a service that is off turns it on for the workspace first.
  const handleCheck = async (option: ServiceOption, checked: boolean) => {
    const off = !!option.policy && !option.policy.is_enabled
    if (checked && off && !(await power.enable(option.service))) return
    toggle(option.service, checked)
  }

  return (
    <Flex
      vertical
      gap={8}
      role="group"
      aria-label={t('workspace.aiConnectors.connect.chooseService')}
    >
      {options.map((option) => {
        const name = t(`workspace.aiConnectors.services.${option.service}.name`)
        const picked = value.includes(option.service)
        const rows = rowsFor(option.service)
        const canTurnOn =
          !!option.policy && !option.policy.is_enabled && !option.blocked
        const busy = power.pending === option.service
        const scopes = scopesFor(option.service)
        const hint = option.blocked
          ? t(
              option.blocked === 'notInstalled'
                ? 'workspace.aiConnectors.servicesTab.needsInstance'
                : 'workspace.aiConnectors.servicesTab.notConfigured',
              { service: name }
            )
          : option.selectable
            ? t(`workspace.aiConnectors.services.${option.service}.description`)
            : canTurnOn
              ? t('workspace.aiConnectors.connect.serviceOffAdmin')
              : t('workspace.aiConnectors.connect.serviceOff')

        return (
          <Flex
            key={option.service}
            vertical
            gap={10}
            className={cx(
              styles.serviceRow,
              picked && styles.serviceRowSelected
            )}
          >
            <Flex align="center" justify="space-between" gap={12}>
              <Checkbox
                checked={picked}
                disabled={disabled || busy || !(option.selectable || canTurnOn)}
                onChange={(e) => void handleCheck(option, e.target.checked)}
              >
                <Flex vertical>
                  <Text strong>{name}</Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {hint}
                  </Text>
                </Flex>
              </Checkbox>
              <Flex align="center" gap={12}>
                {picked && rows.length ? (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {t('workspace.aiConnectors.connect.scopesCount', {
                      count: scopes.size,
                      total: rows.length
                    })}
                  </Text>
                ) : null}
                {busy ? <Spin size="small" /> : null}
                {option.policy?.is_enabled ? (
                  <Button
                    type="link"
                    danger
                    size="small"
                    disabled={disabled || busy}
                    onClick={() => power.disable(option.service)}
                  >
                    {t('workspace.aiConnectors.connect.turnOff')}
                  </Button>
                ) : null}
              </Flex>
            </Flex>

            {picked ? (
              catalog.isLoading ? (
                <ContentSpinner minHeight={80} />
              ) : catalog.isError ? (
                <QueryErrorAlert onRetry={catalog.refetch} />
              ) : rows.length ? (
                <>
                  <ScopePicker
                    hideAppHeader
                    catalog={rows}
                    value={scopes}
                    disabled={disabled}
                    onChange={(next) => onScopesChange(option.service, next)}
                  />
                  {scopes.size === 0 ? (
                    <Text type="warning" style={{ fontSize: 12 }}>
                      {t('workspace.aiConnectors.connect.scopesEmpty', {
                        service: name
                      })}
                    </Text>
                  ) : null}
                </>
              ) : null
            ) : null}
          </Flex>
        )
      })}
    </Flex>
  )
}
